import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  Zap, 
  Crown, 
  Sparkles, 
  ArrowRight,
  Check,
  Coins,
  Lock
} from 'lucide-react';
import BuyCreditsModal from './BuyCreditsModal';
import { trackUpgradeModalShown, trackUpgradeModalClicked, trackButtonClick } from '@/services/analytics';

const UpgradeModal = ({ 
  isOpen, 
  onClose, 
  trigger = 'credits',  // 'credits', 'model', 'feature'
  featureName = '',
  modelName = ''
}) => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [hasTracked, setHasTracked] = useState(false);
  
  // Track modal display
  useEffect(() => {
    if (isOpen && !hasTracked) {
      trackUpgradeModalShown(trigger, token);
      setHasTracked(true);
    }
  }, [isOpen, hasTracked, trigger, token]);
  
  // Reset tracking when modal closes
  useEffect(() => {
    if (!isOpen) setHasTracked(false);
  }, [isOpen]);
  
  const getTriggerContent = () => {
    switch (trigger) {
      case 'credits':
        return {
          icon: <Coins className="w-8 h-8 text-amber-500" />,
          title: "You've run out of credits",
          description: "Purchase more credits or upgrade your plan to keep creating amazing designs.",
          primaryAction: "Buy Credits",
          primaryHandler: () => {
            trackUpgradeModalClicked('buy_credits', token);
            setShowBuyCredits(true);
          }
        };
      case 'model':
        return {
          icon: <Lock className="w-8 h-8 text-purple-500" />,
          title: `${modelName} requires an upgrade`,
          description: "Unlock premium AI models with a Growth or Pro plan for better results.",
          primaryAction: "Upgrade Plan",
          primaryHandler: () => {
            trackUpgradeModalClicked('upgrade_plan', token);
            onClose();
            navigate('/pricing');
          }
        };
      case 'feature':
        return {
          icon: <Sparkles className="w-8 h-8 text-blue-500" />,
          title: `Unlock ${featureName}`,
          description: "This premium feature is available on Growth and Pro plans.",
          primaryAction: "Upgrade Plan",
          primaryHandler: () => {
            onClose();
            navigate('/pricing');
          }
        };
      default:
        return {
          icon: <Zap className="w-8 h-8 text-primary" />,
          title: "Unlock more power",
          description: "Upgrade to access premium features and higher limits.",
          primaryAction: "Upgrade Plan",
          primaryHandler: () => {
            onClose();
            navigate('/pricing');
          }
        };
    }
  };
  
  const content = getTriggerContent();
  
  const plans = [
    { name: 'Growth', price: 29, credits: 500, highlight: true },
    { name: 'Pro', price: 49, credits: 2000, highlight: false }
  ];
  
  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              {content.icon}
            </div>
            <DialogTitle className="text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {content.title}
            </DialogTitle>
            <DialogDescription className="text-center">
              {content.description}
            </DialogDescription>
          </DialogHeader>

          {/* Quick Plans Preview */}
          <div className="space-y-3 my-4">
            {plans.map((plan) => (
              <motion.div
                key={plan.name}
                whileHover={{ scale: 1.01 }}
                className={`relative p-4 rounded-xl border transition-all cursor-pointer ${
                  plan.highlight 
                    ? 'border-primary bg-primary/5 ring-1 ring-primary/20' 
                    : 'border-border hover:border-primary/30'
                }`}
                onClick={() => {
                  onClose();
                  navigate('/pricing');
                }}
              >
                {plan.highlight && (
                  <span className="absolute -top-2 right-4 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                    Most Popular
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {plan.highlight ? (
                      <Crown className="w-5 h-5 text-primary" />
                    ) : (
                      <Zap className="w-5 h-5 text-muted-foreground" />
                    )}
                    <div>
                      <p className="font-semibold">{plan.name}</p>
                      <p className="text-xs text-muted-foreground">{plan.credits} credits/month</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">${plan.price}</p>
                    <p className="text-xs text-muted-foreground">/month</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2 mt-2">
            <Button 
              className="w-full"
              onClick={content.primaryHandler}
              data-testid="upgrade-primary-btn"
            >
              {content.primaryAction}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            
            {trigger === 'credits' && (
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  onClose();
                  navigate('/pricing');
                }}
              >
                View All Plans
              </Button>
            )}
            
            {trigger !== 'credits' && (
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setShowBuyCredits(true)}
              >
                <Coins className="w-4 h-4 mr-2" />
                Just Buy Credits
              </Button>
            )}
          </div>

          {/* Current Credits Display */}
          {user && (
            <div className="mt-4 pt-4 border-t border-border text-center text-sm text-muted-foreground">
              Current balance: <strong className="text-foreground">{user.credits}</strong> credits
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      <BuyCreditsModal 
        isOpen={showBuyCredits} 
        onClose={() => setShowBuyCredits(false)} 
      />
    </>
  );
};

export default UpgradeModal;
